/**
 * Customer type on the cart: private or business, with EAN invoicing as an extra choice inside
 * the business branch.
 *
 * Liquid renders every field and disables the ones that do not apply. This decides which are in
 * play and persists what is entered to the cart.
 *
 * Why disabled rather than hidden alone: a disabled control is neither validated nor submitted.
 * One attribute therefore both lifts `required` off fields that do not apply and keeps their
 * values out of the checkout POST - so a private order cannot carry a CVR number.
 *
 * Why the cart is updated at all, when the attributes[] inputs already submit with checkout:
 * the drawer and the cart page are two separate forms, and the drawer is re-rendered from the
 * server whenever a quantity changes. Persisting on change means what was typed in one survives
 * both. Clearing matters just as much - Shopify keeps an attribute it was not sent, so switching
 * type has to remove the old one explicitly.
 */
(function () {
  'use strict';

  /* Attribute names, matching cart-customer-type.liquid. Fixed strings, never translated - they
     are the keys the values live under on the cart. */
  const BUSINESS = ['CVR-nummer'];
  const EAN = 'EAN-GLN-nummer';
  const ALL = BUSINESS.concat([EAN]);

  /**
   * @param {Object} attributes Attribute name to value. An empty string removes an attribute.
   */
  async function updateCart(attributes) {
    try {
      await fetch(`${(window.theme && theme.routes && theme.routes.cartUpdate) || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      });
    } catch (error) {
      /* The cart could not be reached. Nothing is lost: the fields still hold what was typed and
         still submit with the checkout POST, which is what puts them on the order. This request
         only buys persistence across a re-render. */
    }
  }

  class CartCustomerType extends HTMLElement {
    connectedCallback() {
      this.radios = Array.from(this.querySelectorAll('[data-customer-type]'));
      this.businessFields = this.querySelector('[data-customer-type-fields="erhverv"]');
      this.eanToggle = this.querySelector('[data-ean-toggle]');
      this.eanFields = this.querySelector('[data-ean-fields]');

      this.onTypeChange = this.onTypeChange || this.handleTypeChange.bind(this);
      this.onEanChange = this.onEanChange || this.handleEanChange.bind(this);
      this.onFieldChange = this.onFieldChange || this.persist.bind(this);

      this.radios.forEach((radio) => radio.addEventListener('change', this.onTypeChange));
      if (this.eanToggle) this.eanToggle.addEventListener('change', this.onEanChange);

      /* change, not input: it fires once the field is left, so the cart is not written on every
         keystroke and a re-render cannot land mid-word. */
      this.querySelectorAll('input[name^="attributes["]').forEach((input) => {
        if (!input.dataset.customerType) input.addEventListener('change', this.onFieldChange);
      });

      /* Digits only, as they are typed. The pattern attribute already rejects anything else, but
         rejecting is a poor answer to a CVR number pasted as "DK 33 39 58 76" - the digits are
         right there. Stripping turns that into a valid value instead of an error. */
      this.onDigitsInput = this.onDigitsInput || this.handleDigitsInput.bind(this);
      this.querySelectorAll('input[data-digits]').forEach((input) => {
        input.addEventListener('input', this.onDigitsInput);
      });

      /* Reflect whatever Liquid rendered, so a saved choice is usable immediately. */
      this.apply(false);
    }

    disconnectedCallback() {
      this.radios.forEach((radio) => radio.removeEventListener('change', this.onTypeChange));
      if (this.eanToggle && this.onEanChange) this.eanToggle.removeEventListener('change', this.onEanChange);
      if (this.onDigitsInput) {
        this.querySelectorAll('input[data-digits]').forEach((input) => {
          input.removeEventListener('input', this.onDigitsInput);
        });
      }
    }

    /**
     * Keeps a numeric field to digits only, up to its stated length.
     * @param {Event} event Input event.
     */
    handleDigitsInput(event) {
      const input = event.target;
      const max = parseInt(input.dataset.digits, 10);
      const cleaned = input.value.replace(/\D/g, '').slice(0, max);

      if (cleaned === input.value) return;

      /* Keep the caret where the typing was, counting only the digits before it - otherwise
         cleaning a character in the middle throws the cursor to the end. */
      const digitsBeforeCaret = input.value.slice(0, input.selectionStart).replace(/\D/g, '').length;
      input.value = cleaned;
      const caret = Math.min(digitsBeforeCaret, cleaned.length);
      input.setSelectionRange(caret, caret);
    }

    /**
     * @returns {boolean} True when the business branch is in play.
     */
    isBusiness() {
      if (this.dataset.lockedType) return true;
      const checked = this.radios.find((radio) => radio.checked);
      return !!checked && checked.dataset.customerType === 'erhverv';
    }

    /**
     * @returns {boolean} True when EAN invoicing is ticked, within the business branch.
     */
    usesEan() {
      return this.isBusiness() && !!this.eanToggle && this.eanToggle.checked;
    }

    /**
     * @returns {string} The Kundetype value to store, or '' when nothing is chosen.
     */
    selectedValue() {
      if (this.dataset.lockedValue) return this.dataset.lockedValue;
      const checked = this.radios.find((radio) => radio.checked);
      return checked ? checked.value : '';
    }

    handleTypeChange() {
      this.apply(true);
      this.persist();
    }

    handleEanChange() {
      this.apply(true);
      this.persist();
    }

    /**
     * Shows the groups the current choice needs and disables the rest.
     * @param {boolean} focusFirst Move focus into a group that has just appeared.
     */
    apply(focusFirst) {
      const business = this.isBusiness();
      const ean = this.usesEan();

      if (this.businessFields) {
        this.businessFields.hidden = !business;
        BUSINESS.forEach((name) => {
          const input = this.querySelector(`input[name="attributes[${name}]"]`);
          if (input) input.disabled = !business;
        });
      }

      if (this.eanFields) {
        this.eanFields.hidden = !ean;
        const input = this.querySelector(`input[name="attributes[${EAN}]"]`);
        if (input) input.disabled = !ean;
      }

      if (focusFirst && business) {
        const target = ean
          ? this.querySelector(`input[name="attributes[${EAN}]"]:not([disabled])`)
          : this.querySelector(`input[name="attributes[${BUSINESS[0]}]"]:not([disabled])`);
        if (target) target.focus();
      }
    }

    /**
     * Sends the whole state in one request, never a single field.
     *
     * Found by testing this in a browser: filling two fields in a row fired two overlapping
     * requests, and the second one landed the cart back without the first one's value - cart
     * updates are not transactional across concurrent requests. One payload built from the
     * current DOM has no such race, and it also makes clearing part of the same write: every
     * attribute the current choice does not use is sent empty, which is how Shopify removes one.
     * Without that, switching from Erhverv to Privat would leave the CVR number on the order,
     * since an attribute that is not sent is kept.
     */
    persist() {
      clearTimeout(this.persistTimer);

      /* Coalesces a burst - tabbing through the fields, or a radio click followed immediately by
         typing - into a single write. */
      this.persistTimer = setTimeout(() => {
        const keep = [];
        if (this.isBusiness()) keep.push.apply(keep, BUSINESS);
        if (this.usesEan()) keep.push(EAN);

        const attributes = { Kundetype: this.selectedValue() };

        ALL.forEach((name) => {
          if (keep.indexOf(name) === -1) {
            attributes[name] = '';
            return;
          }
          const input = this.querySelector(`input[name="attributes[${name}]"]`);
          attributes[name] = input ? input.value : '';
        });

        updateCart(attributes);
      }, 250);
    }
  }

  if (!customElements.get('cart-customer-type')) {
    customElements.define('cart-customer-type', CartCustomerType);
  }
})();
